import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AxiosInstance from "../Home/AxiosInstance";
import { clearAuthSession } from "../Home/auth";
import Profile from "../Home/Profile.png";
import CompanyLogo from "./CompanyLogo.png";
import "bootstrap/dist/css/bootstrap.min.css";
import "./EmployerHeader.css";

export default function UserHeader() {
  const [userId, setUserId] = useState(null);
  const [user, setUser] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [role, setRole] = useState(null);
  const [businessType, setBusinessType] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isNotificationModalOpen, setNotificationModalOpen] = useState(false);

  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

const BASE_MEDIA_URL = `${AxiosInstance.defaults.baseURL}media/`;
const profileImageSrc = user?.image_path
  ? `${BASE_MEDIA_URL}${user.image_path.replace(/\\/g, "/")}`
  : Profile;

  const isProfilePage = location.pathname === "/profile";

  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    const storedRole = localStorage.getItem("role");

    if (storedUserId) {
      setUserId(storedUserId);
      setRole(storedRole);
      fetchUserDetails(storedUserId);
      fetchNotifications(storedUserId);
    }
  }, []);

  const fetchUserDetails = async (id) => {
    try {
      const response = await AxiosInstance.get(
        `companies/company/user/${id}/`
      );
      const data = response.data.company_users[0];
      setUser(data.user);
      setCompanyName(data.company.company_name);
      setBusinessType(data.company.business_type);
      localStorage.setItem("companyId", data.company.company_id);
    } catch (error) {
      console.error("Error fetching user details", error);
    }
  };

  const fetchNotifications = async (userId) => {
    try {
      const res = await AxiosInstance.get(
        `companies/notification/${userId}`
      );
      const inAppNotifications = res.data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setNotifications(inAppNotifications);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await AxiosInstance.delete(
        `companies/notifications/${notificationId}`
      );
      setNotifications((prev) =>
        prev.filter((n) => n.notification_id !== notificationId)
      );
    } catch (error) {
      console.error("Error deleting notification:", error);
      alert("Failed to delete the notification. Please try again.");
    }
  };

  const toggleDropdown = () => {
    setDropdownVisible((prev) => !prev);
    setNotificationModalOpen(false);
  };

  const openNotificationModal = () => {
    setNotificationModalOpen(true);
    setDropdownVisible(false);
  };

  const closeNotificationModal = () => {
    setNotificationModalOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !dropdownRef.current?.contains(e.target) &&
        !notificationRef.current?.contains(e.target)
      ) {
        setDropdownVisible(false);
      }
      if (
        isNotificationModalOpen &&
        notificationRef.current &&
        !notificationRef.current.contains(e.target)
      ) {
        closeNotificationModal();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [isNotificationModalOpen]);

  const handleDetails = () => {
    navigate("/my-details");
    setDropdownVisible(false);
  };

  const handleProfile = () => {
    navigate("/my-profile");
    setDropdownVisible(false);
  };

  const handleChangePassword = () => {
    navigate("/change-password", {
      state: {
        userType: "user",
        userId: userId,
      },
    });
    setDropdownVisible(false);
  };

  const handleSignOut = () => {
    clearAuthSession();
    setDropdownVisible(false);
    if (businessType === 4) {
      navigate("/institutions");
    } else {
      navigate("/employers");
    }
  };

  const isInstitutionPage =
    location.pathname.includes("institution") || businessType === 4;

  return (
    <header className="employer-header">
      <div className="employer-header-inner">
        <div className="employer-brand">
          <img
            src={CompanyLogo}
            alt=""
            className="employer-brand-logo round"
          />
          <span className="employer-brand-text">
            <strong>{companyName || (isInstitutionPage ? "Institute" : "Company")}</strong>
            <small>
              {isInstitutionPage ? "Institute portal" : "Employer portal"}
            </small>
          </span>
        </div>

        {!isProfilePage && user && (
          <div className="employer-header-user" ref={dropdownRef}>
            <button
              type="button"
              className="employer-user-btn"
              onClick={toggleDropdown}
              aria-expanded={dropdownVisible}
            >
              <span className="employer-user-meta">
                <strong>
                  {`${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                    "Employee"}
                </strong>
                <small>{role || "Employee"}</small>
              </span>
              <img
                src={profileImageSrc}
                alt=""
                className="employer-profile-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = Profile;
                }}
              />
            </button>

            {dropdownVisible && (
              <div className="employer-dropdown">
                <button type="button" onClick={handleDetails}>
                  My Details
                </button>
                <button type="button" onClick={handleProfile}>
                  My Profiles
                </button>
                <button type="button" onClick={openNotificationModal}>
                  Notifications
                  {notifications.length > 0 && (
                    <span className="employer-notif-count">
                      {notifications.length}
                    </span>
                  )}
                </button>
                <button type="button" onClick={handleChangePassword}>
                  Change Password
                </button>
                <button
                  type="button"
                  className="employer-signout"
                  onClick={handleSignOut}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {isNotificationModalOpen && (
        <>
          <div
            className="employer-notif-backdrop"
            onClick={closeNotificationModal}
          />
          <aside className="employer-notif-panel" ref={notificationRef}>
            <div className="employer-notif-head">
              <h5>Notifications</h5>
              <button
                type="button"
                onClick={closeNotificationModal}
                aria-label="Close notifications"
              >
                ×
              </button>
            </div>
            {notifications.length === 0 ? (
              <p className="employer-notif-empty">No notifications yet</p>
            ) : (
              <ul>
                {notifications.map((n) => (
                  <li key={n.notification_id}>
                    <span>{n.message}</span>
                    <button
                      type="button"
                      onClick={() =>
                        handleDeleteNotification(n.notification_id)
                      }
                      aria-label="Delete notification"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </>
      )}
    </header>
  );
}
